import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { CssBaseline } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { KAIZEN_THEME } from "@platform/feature-flags";
import { useFlagResolved } from "@platform/feature-flags/client";
import { useStorage } from "@platform/hooks";
import { useEffect, useState } from "react";

import { kaizenComposedTheme, theme } from "./new-theme";

import type { ReactNode } from "react";

/** The attribute MUI stamps on <html> with the Kaizen theme's active scheme. */
const COLOR_SCHEME_ATTRIBUTE = "data-mui-color-scheme";

/** The flag's last resolved value, so the next load paints the right theme first. */
export const REMEMBERED_THEME_KEY = "centro.kaizen-theme";

const rememberedValue = (value: unknown): boolean | null =>
  typeof value === "boolean" ? value : null;

/**
 * Mounts the app theme or the Kaizen one on the KAIZEN_THEME flag.
 *
 * The flag resolves after the first render, so two things keep that from
 * showing: one ThemeProvider whose `theme` prop changes, since swapping
 * provider components would remount the whole app, and the last resolved
 * value remembered in localStorage, so a repeat load paints the right theme
 * from the first frame. Locally, force the flag with
 * VITE_FORCE_FLAGS=kaizen-theme.
 */
export const ThemeSelector = ({
  children,
}: {
  children: ReactNode;
}): ReactNode => {
  const flagOn = useFeatureIsOn(KAIZEN_THEME);
  const flagResolved = useFlagResolved(KAIZEN_THEME);
  const { getStorageItem, setStorageItem } = useStorage("local");
  const [remembered] = useState(() =>
    rememberedValue(getStorageItem(REMEMBERED_THEME_KEY)),
  );

  const kaizenEnabled =
    flagResolved || remembered === null ? flagOn : remembered;

  useEffect(() => {
    if (!flagResolved) return;
    setStorageItem(REMEMBERED_THEME_KEY, flagOn);
  }, [flagResolved, flagOn, setStorageItem]);

  // Only the Kaizen theme writes the scheme attribute, and MUI never removes
  // it. Left behind after a swap back to the legacy theme, a "dark" value
  // would keep the design system's dark rules applied to a light page.
  useEffect(() => {
    if (kaizenEnabled) return;
    document.documentElement.removeAttribute(COLOR_SCHEME_ATTRIBUTE);
  }, [kaizenEnabled]);

  return (
    <ThemeProvider
      theme={kaizenEnabled ? kaizenComposedTheme : theme}
      defaultMode="light"
    >
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
};
